import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CartService } from 'src/app/services/cart.service';
import { NavigatorService } from 'src/app/services/navigator.service';
import { UserAdditionalDataService } from 'src/app/services/user-additional-data.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { UserOrdersService } from 'src/app/services/user-orders.service';
import { InfoDialogComponent } from '../../common/info-dialog/info-dialog.component';
import { YesNoDialogComponent } from '../../common/yes-no-dialog/yes-no-dialog.component';
import { PromoCampaignService } from 'src/app/services/promo-campaign.service';
import { AlertService } from '../../common/alert/alert.service';
import { environment } from 'src/environments/environment';
import { InpostParcelLocker, normalizeInpostPoint } from 'src/app/services/inpost-parcel-locker.model';

@Component({
  selector: 'app-cart-view',
  templateUrl: './cart-view.component.html',
  styleUrls: ['./cart-view.component.css']
})
export class CartViewComponent implements OnInit {

  public isSpinnerVisible: boolean = false;

  // Publiczny token Geowidgetu z plików środowiskowych (bezpieczny we froncie).
  public readonly geowidgetToken: string = environment.inpostGeowidgetToken;

  // Strażnik jednokrotnego zbudowania widgetu. Kontener jest pod *ngIf, więc
  // setter ViewChild odpala się gdy element pojawia się/znika z widoku.
  private geowidgetBuilt: boolean = false;

  // Kontener na <inpost-geowidget>. Setter (a nie ngAfterViewInit), bo div żyje
  // pod *ngIf i może pojawić się dopiero gdy koszyk zdąży się załadować.
  @ViewChild('geowidgetContainer')
  public set geowidgetContainer(ref: ElementRef<HTMLElement> | undefined) {
    if (!ref) {
      // Element zniknął z DOM (np. złożono zamówienie) — pozwól zbudować ponownie.
      this.geowidgetBuilt = false;
      return;
    }
    if (this.geowidgetBuilt) {
      return;
    }
    this.geowidgetBuilt = true;
    this.buildGeowidget(ref.nativeElement);
  }

  public get cartItemsSource() {
    return this.cartService?.cartItems ?? [];
  }

  public get userAssignedPoints() {
    return this.cartService.userPoints;
  }

  public get userAvailablePoints() {
    return this.cartService.userAvailablePoints;
  }

  // Wybrany Paczkomat (źródło prawdy w CartService, by czyścił się z koszykiem).
  public get selectedParcelLocker(): InpostParcelLocker | null {
    return this.cartService.selectedParcelLocker;
  }

  public get isParcelLockerSelected(): boolean {
    return !!this.selectedParcelLocker?.name;
  }

  // Komunikat walidacyjny pod mapą: koszyk ma pozycje, ale Paczkomat nie wybrany.
  public get showParcelLockerValidationError(): boolean {
    return !this.didUserPlaceOrder
      && this.cartItemsSource.length > 0
      && !this.isParcelLockerSelected;
  }

  public get isPlaceOrderButtonEnabled() {
    if (this.userDataService.didUserPlacedOrder) {
      return false;
    }
    if (!this.isParcelLockerSelected) {
      return false;
    }
    return this.cartService?.cartItems?.length > 0;
  }

  public get didUserPlaceOrder() {
    return this.userDataService.didUserPlacedOrder;
  }

  constructor(private cartService: CartService,
    private navigator: NavigatorService,
    private userOrdersService: UserOrdersService,
    private userDataService: UserDataService,
    private userAdditionalDataService: UserAdditionalDataService,
    private dialog: MatDialog,
    public promoCampaignService: PromoCampaignService,
    private alertService: AlertService
  ) {
  }

  async ngOnInit(): Promise<void> {
    if(this.promoCampaignService.usersMessageActive){
      this.alertService.error(this.promoCampaignService.userMessage, {alias: "test", autoClose: false, keepAfterRouteChange: true, priority: 1});
    }
  }

  public goToBenefitsCatalog() : void {
    this.navigator.redirectToBenefitsScreen();
  }

  // Buduje custom element <inpost-geowidget> imperatywnie.
  // KLUCZOWE: atrybut `token` MUSI być ustawiony ZANIM element trafi do DOM.
  // Geowidget v5 czyta token w connectedCallback (raz, przy podpięciu do DOM) i
  // nie reaguje na późniejszą zmianę atrybutu. Deklaratywne [attr.token] w Angularze
  // jest zapisywane dopiero w update pass — czyli PO wstawieniu elementu — więc
  // widget łapał `null` i wysyłał "Authorization: Bearer null" => 403.
  // setAttribute('token', ...) przed appendChild gwarantuje token w momencie connectu.
  private buildGeowidget(container: HTMLElement): void {
    customElements.whenDefined('inpost-geowidget').then(() => {
      container.innerHTML = '';
      const widget = document.createElement('inpost-geowidget');
      widget.setAttribute('token', this.geowidgetToken);
      widget.setAttribute('language', 'pl');
      widget.setAttribute('config', 'parcelCollect');
      // Obsługa kliknięcia "Wybierz". Geowidget v5 NIE emituje statycznego eventu
      // "onpoint": przy wyborze punktu odsyła z iframe wartość atrybutu `onpoint`
      // i albo woła window[<nazwa>], albo dispatchuje CustomEvent o NAZWIE równej
      // tej wartości. Dodatkowo `&onpoint=` trafia do URL iframe tylko gdy atrybut
      // jest ustawiony — bez tego przycisk "Wybierz" nie zgłasza wyboru w ogóle.
      // Dlatego atrybut i listener MUSZĄ używać tej samej, unikalnej nazwy, a atrybut
      // musi być ustawiony przed appendChild (iframe budowany jest w connectedCallback).
      const POINT_SELECT_EVENT = 'panaStoreOnPointSelect';
      widget.setAttribute('onpoint', POINT_SELECT_EVENT);
      widget.style.display = 'block';
      widget.style.width = '100%';
      widget.style.height = '100%';
      widget.addEventListener(POINT_SELECT_EVENT, (event: Event) => {
        this.onParcelLockerSelected(event as CustomEvent);
      });
      container.appendChild(widget);
    });
  }

  // Handler eventu `onpoint` z <inpost-geowidget> (Geowidget v5).
  // event.detail to surowy obiekt punktu — normalizujemy do naszego modelu
  // i zapisujemy w koszyku, by przetrwał aż do złożenia/wyczyszczenia zamówienia.
  public onParcelLockerSelected(event: any): void {
    const point = event?.detail ?? event;
    // Log diagnostyczny: SUROWE dane punktu prosto z Geowidgetu (event.detail) —
    // pełny obiekt ze wszystkimi polami (współrzędne, godziny, typ skrytek itd.),
    // zanim zredukujemy je do naszego modelu.
    console.log('[onParcelLockerSelected] Surowy event z Geowidgetu:', event);
    console.log('[onParcelLockerSelected] Surowy punkt (event.detail):', point);
    const normalized = normalizeInpostPoint(point);
    console.log('[onParcelLockerSelected] Po normalizacji:', normalized);
    this.cartService.setSelectedParcelLocker(normalized);
  }

  public placeOrder() : void {
    // Log diagnostyczny: dane wybranego Paczkomatu z Geowidgetu (znormalizowany
    // model zapisany w koszyku) w momencie inicjowania składania zamówienia.
    console.log('[placeOrder] Wybrany Paczkomat (Geowidget):', this.selectedParcelLocker);
    console.log('[placeOrder] Czy Paczkomat wybrany:', this.isParcelLockerSelected);
    this.dialog.open(YesNoDialogComponent, { disableClose: true, data: {
      title: 'Składanie zamówienia',
      message: 'Każdy użytkownik może złożyć zamówienie wyłącznie raz. ' +
        'Nie ma możliwości modyfikacji zamówienia, ani jego anulowania. ' +
        'Czy złożyć zamówienie z obecnym stanem koszyka?',
      confirmFunction: this.executePlaceOrder
    } });
  }

  private executePlaceOrder = async () : Promise<void> => {
    // Twarda walidacja wyboru Paczkomatu (poza wyłączonym przyciskiem) — gdyby
    // ktoś ominął UI. Wiążącą walidację i tak wykonuje backend.
    if (!this.isParcelLockerSelected) {
      this.displayParcelLockerRequiredInfo();
      return;
    }
    this.isSpinnerVisible = true;
    // Typ zamówienia wynika z kampanii koszyka (I2), a nie z globalnego stanu.
    // Brak kampanii (pusty/neutralny koszyk) => twarda odmowa (I4).
    const campaignType = this.cartService.campaignType;
    if (this.userDataService?.currentUser && campaignType) {
      const orderPlaceResult = await this.userOrdersService.placeUserOrder(
        campaignType, this.cartService, this.userDataService.currentUser, this.userDataService, this.userAdditionalDataService);
      if (orderPlaceResult) {
        this.handleSuccessfullOrderPlacement();
      } else {
        this.displayOrderPlacementErrorInfo();
      }
    } else {
      this.displayOrderPlacementErrorInfo();
    }
    this.isSpinnerVisible = false;
  }

  private displayOrderPlacementErrorInfo() : void {
    this.dialog.open(InfoDialogComponent, { disableClose: true, data: {
      title: 'Błąd',
      message: 'Nie udało się złożyć zamówienia. Spróbuj ponownie później.',
      isErrorInfo: true,
      okFunc: null
    } });
  }

  private displayParcelLockerRequiredInfo() : void {
    this.dialog.open(InfoDialogComponent, { disableClose: true, data: {
      title: 'Wybierz Paczkomat',
      message: 'Aby złożyć zamówienie, wybierz Paczkomat InPost na mapie.',
      isErrorInfo: true,
      okFunc: null
    } });
  }

  private handleSuccessfullOrderPlacement() : void {
    this.dialog.open(InfoDialogComponent, { disableClose: true, data: {
      title: 'Sukces',
      message: 'Twoje zamówienie zostało zarajestrowane. ' +
        'Na Twój adres email zostało wysłane potwierdzenie złożenia zamówienia. ' +
        'Od tej pory nie będziesz mieć możliwości złożenia kolejnego zamówienia.',
      isErrorInfo: false,
      okFunc: this.executeAfterOrderPlacedWork
    } });
  }

  private executeAfterOrderPlacedWork = () : void => {
    this.cartService.resetCartState();
    this.navigator.redirectToBenefitsScreen();
  }
}
